import {conforms, filter, find, findLast, flatten, includes, keyBy, map, memoize, reject, uniqBy} from 'lodash';
import {getRegionSelector} from 'modules/selectors/settings';
import {
	IGlobalStore,
	ITriviaContest,
	ITriviaContests, ITriviaQuestion,
	ITriviaRound,
	MemoizedContextUndefined,
	Optional
} from 'modules/types';
import {IS_LOC} from 'modules/utils/constants';
import {getMonthAndYearOfDate} from 'modules/utils/date';
import {EnumRegion, EnumTriviaContestStatus, EnumTriviaStatusType} from 'modules/utils/enums';
import {createSelector} from 'reselect';

export const getTriviaContestsCommon = (store: IGlobalStore): ITriviaContests => store.contests_trivia;

export const getTriviaContests = createSelector<IGlobalStore, ITriviaContests, EnumRegion, ITriviaContests>(
	[getTriviaContestsCommon, getRegionSelector],
	(contests, region) => {
		return filter(contests, (contest: ITriviaContest) => {
			return contest.region === region;
		});
	}
);

export const getTriviaContestByID = createSelector<IGlobalStore, ITriviaContests, MemoizedContextUndefined<number, ITriviaContest>>(
	[getTriviaContests],
	(contests) => memoize((id: number) => {
		return find(contests, (contest: ITriviaContest) => {
			return contest.id === id;
		});
	})
);

export const getLastCompletedContest = createSelector<IGlobalStore, ITriviaContests, Optional<ITriviaContest>>(
	[getTriviaContests],
	(contests) => {
		return findLast(contests, conforms({
			status: (v: EnumTriviaContestStatus) => includes([EnumTriviaContestStatus.complete], v)
		}));
	}
);

export const getCompletedContests = createSelector<IGlobalStore, ITriviaContests, ITriviaContests>(
	[getTriviaContests],
	(contests) => {
		return filter(contests, conforms({
			status: (v: EnumTriviaContestStatus) => includes([EnumTriviaContestStatus.complete], v)
		}));
	}
);

export const getCompletedNightContests = createSelector<IGlobalStore, ITriviaContests, ITriviaContests>(
	[getCompletedContests],
	(contests) => {
		return filter(contests, (contest) => {
			return contest.is_herd
		});
	}
);

export const getCompletedDailyContests = createSelector<IGlobalStore, ITriviaContests, ITriviaContests>(
	[getCompletedContests],
	(contests) => {
		return reject(contests, (contest) => {
			return contest.is_herd
		});
	}
);

/**
 * Returns contest which is should be displayed based on
 * start_date_time minus 15 mins - as live state is 15 mins out before contest starts
 * end_date_time_number
 *
 * if contest was voided the warning message should be shown for next 2 minutes
 */
export const getCurrentContestByTime = createSelector<IGlobalStore, ITriviaContests, Optional<ITriviaContest>>(
	[getTriviaContests],
	(contests) => {
		const contest_actual = findLast(contests, (contest) => {
			const ONE_MIN = 60 * 1000;
			const current_date_number = +new Date();
			const start_date_time_number = +new Date(contest.start_date_time) - 15 * ONE_MIN;

			if (contest.voided_at) {
				const voided_at_number = +new Date(contest.voided_at) + 2 * ONE_MIN;
				return current_date_number >= start_date_time_number && current_date_number <= voided_at_number;
			}

			const end_date_time_number = +new Date(contest.end_date_time);
			return current_date_number >= start_date_time_number && current_date_number <= end_date_time_number;
		});

		if (!contest_actual || IS_LOC) {
			return;
		}

		return contest_actual;
	}
);

export const getLastGameContest = createSelector<IGlobalStore, ITriviaContests, Optional<ITriviaContest>>(
	[getTriviaContests],
	(contests) => {
		return findLast(contests, conforms({
			status: (v: EnumTriviaContestStatus) => includes(
				[
					EnumTriviaContestStatus.live,
					EnumTriviaContestStatus.complete
				], v)
		}));
	}
);

export const getReplayedContest = createSelector<IGlobalStore, ITriviaContests, Optional<ITriviaContest>>(
	[getTriviaContests],
	(contests) => {
		return find(contests, 'is_replayed');
	}
);

export const getActualContest = createSelector<IGlobalStore, ITriviaContests, Optional<ITriviaContest>>(
	[getTriviaContests],
	(contests) => {
		return find(contests, conforms({
			status: (v: EnumTriviaContestStatus) => includes([EnumTriviaContestStatus.scheduled, EnumTriviaContestStatus.live], v)
		}));
	}
);

export const getActualContestId = createSelector<IGlobalStore, Optional<ITriviaContest>, number>(
	[getActualContest],
	(contest) => {
		const contest_id = contest?.id || 0;
		return memoize((id) => contest_id)(contest_id);
	}
);

export const getAllRounds = createSelector<IGlobalStore, ITriviaContests, ITriviaRound[]>(
	[getTriviaContests],
	(contests) => {
		return flatten(map(contests, 'rounds'));
	}
);

export const getLastGameRound = createSelector<IGlobalStore, ITriviaRound[], Optional<ITriviaRound>>(
	[getAllRounds],
	(rounds) => {
		return findLast(rounds, conforms({
			status: (v: EnumTriviaStatusType) => includes([EnumTriviaStatusType.complete, EnumTriviaStatusType.live], v)
		}));
	}
);

export const getLiveRound = createSelector<IGlobalStore, ITriviaRound[], Optional<ITriviaRound>>(
	[getAllRounds],
	(rounds) => {

		return find(rounds, {
			status: EnumTriviaStatusType.live
		});
	}
);

export const getActualContestRound = createSelector<IGlobalStore, ITriviaRound[], Optional<ITriviaRound>>(
	[getAllRounds],
	(rounds) => {

		return find(rounds, conforms({
			status: (v: EnumTriviaStatusType) => includes([EnumTriviaStatusType.scheduled, EnumTriviaStatusType.live], v)
		}));
	}
);

export const getScheduledRound = createSelector<IGlobalStore, ITriviaRound[], Optional<ITriviaRound>>(
	[getAllRounds],
	(rounds) => {
		return find(rounds, conforms({
			status: (v: EnumTriviaStatusType) => includes([EnumTriviaStatusType.scheduled], v)
		}));
	}
);

/**
 * Get dates of all rounds as  `month-year`
 */
export const getRoundsDates = createSelector<IGlobalStore, ITriviaRound[], ITriviaRound[]>(
	[getAllRounds],
	(rounds) => {
		return uniqBy(rounds, (round) => {
			return getMonthAndYearOfDate(round.start_date_time)
		})
	}
);

export const getLastCompletedRound = createSelector<IGlobalStore, ITriviaRound[], Optional<ITriviaRound>>(
	[getAllRounds],
	(rounds) => {
		return findLast(rounds, conforms({
			status: (v: EnumTriviaStatusType) => includes([EnumTriviaStatusType.complete], v)
		}));
	}
);

export const getRoundByIDSelector = createSelector<IGlobalStore, ITriviaRound[], (id: number) => Optional<ITriviaRound>>(
	[getAllRounds],
	(rounds) => {
		const rounds_by_id = keyBy(rounds, 'id');
		return memoize((id: number) => rounds_by_id[id])
	}
);

export const getLiveQuestion = createSelector<IGlobalStore, Optional<ITriviaRound>, Optional<ITriviaQuestion>>(
	[getActualContestRound],
	(round) => {
		return findLast(round?.questions, {
			status: EnumTriviaStatusType.live
		});
	}
);

export const getLastCompletedQuestionOfRound = createSelector<IGlobalStore, Optional<ITriviaRound>, Optional<ITriviaQuestion>>(
	[getActualContestRound],
	(round) => {
		return find(round?.questions, {
			status: EnumTriviaStatusType.complete
		});
	}
);


export const getScheduledQuestionOfRound = createSelector<IGlobalStore, Optional<ITriviaRound>, Optional<ITriviaQuestion>>(
	[getActualContestRound],
	(round) => {

		return find(round?.questions, {
			status: EnumTriviaStatusType.scheduled
		});
	}
);

/**
 * Get contest which contains roundId
 */
export const getContestByRoundIDSelector = createSelector<IGlobalStore, Optional<ITriviaContests>, (round_id: number) => Optional<ITriviaContest>>(
	[getTriviaContests],
	(contests) => {
		return memoize((round_id: number) => find(contests, (contest) => {
			return !!find(contest.rounds, {
				id: round_id
			});
		}))
	}
);

